import {useSnackbar} from 'notistack'
import React, {createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {AreaItem, ObjectItem} from 'src/common/models/detection-objects/DetectionObjectsDTO'
import {Pos} from 'src/common/types/generics/ImageDetections'
import useDebounce from 'src/hooks/useDebounce'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import mkKey from 'src/utilities/mkKey'
import api, {BarrierType, Camera, CameraObject, ConnectionTimeMultiple} from '../../../services/api'
import handleErrorWithSnackbar from '../../../utilities/handleErrorWithSnackbar'
import {AxiosErrorGeneric} from '../../../utilities/RequestError'

type Page = {
  page: number
  pageSize: number
}
type AddAreaItem = {
  name: string
  points: Pos[]
}
type AreaData = AreaItem<{
  area?: BarrierType
  updated?: boolean
  adding?: boolean
}>
type ObjectData = ObjectItem<{
  selected?: boolean
  object?: CameraObject
}>

export const initalCameraFilterStatus = ['Armado', 'Desarmado', 'Fora do horário']
export const initalCameraFilterSituation = ['Habilitada', 'Desabilitada']

type CamerasContextData = {
  loadingCameraStatus: boolean
  toggleStatusFilter: (status: string) => void
  toggleSituationFilter: (status: string) => void
  cameraStatusFilter?: string[]
  cameraSituationFilter?: string
  showOptionsButton: boolean
  deleteCameras: () => Promise<void>
  setLoadingCameraStatus: Dispatch<SetStateAction<boolean>>
  cameraStatusFilterDebounced: any
  setCamerasSearch: (value: string) => void
  camerasSearch: string
  loadingCameras: boolean
  setLoadingCameras: (isloading: boolean) => void
  camerasTotalCount: number
  camerasPageChange: (page: Partial<Page>) => void
  selectedCamerasIds: number[]
  setSelectedCamerasIds: (ids: number[]) => void
  camerasPage: Page
  setCamerasPage: (cameraPage: number) => void
  setCamerasPageSize: (pageSize: number) => void
  cameras: Camera[]
  reloadCameras: () => void
  objectItems: ObjectItem[]
  toggleObject: (key: string) => void
  toggleAllObjects: () => void
  areaItems: AreaItem[]
  addInterestArea: (area: AddAreaItem) => void
  deleteInterestArea: (area: AreaItem) => void
  editInterestArea: (area: AreaItem) => void
  searchFilterSelected: string
  setSearchFilterSelected: (searchFilter: string) => void
  partition: string
  setPartition: (partition: string) => void
  timeData: {[x: string]: number}
  setCamerasStatus: (option: boolean) => void
  setSortByLastDetection: (sort: boolean | undefined) => void
}

export const CamerasContext = createContext({} as CamerasContextData)
const CamerasProvider = ({children}: React.PropsWithChildren<any>) => {
  const {enqueueSnackbar} = useSnackbar()

  const {selectedCompanies} = useCompaniesSelector()

  const location = useLocation()

  const [loadingCameras, setLoadingCameras] = useState(true)
  const [camerasPage, _setCamerasPage] = useState({page: 1, pageSize: 20})
  const [camerasTotalCount, setCamerasTotalCount] = useState(0)
  const [selectedCamerasIds, setSelectedCamerasIds] = useState<number[]>([])
  const [cameras, setCameras] = useState<Camera[]>([])
  const [reloadCamerasCount, setReloadCamerasCount] = useState(0)
  const [chosenCameraStatusFilter, setChosenCameraStatusFilter] = useState<string[]>([])
  const cameraStatusFilterDebounced = useDebounce(chosenCameraStatusFilter, 1000)
  const [chosenCameraFilterSituation, setChosenCameraFilterSituation] = useState<boolean>()
  const cameraSituationFilterDebounced = useDebounce(chosenCameraFilterSituation, 1000)
  const [loadingCameraStatus, setLoadingCameraStatus] = useState(false)
  const [objectItems, setObjectItems] = useState<ObjectData[]>([])
  const [areaItems, setAreaItems] = useState<AreaData[]>([])
  const [areaItemsDeleting, setAreaItemsDeleting] = useState<AreaData[]>([])
  const [partition, setPartition] = useState<string>('')
  const [timeData, setTimeData] = useState<{[x: string]: number}>({})
  const blockLoadingCameras = useRef(false)

  const handleUrlFiltersOnSearch = () => {
    const params = new URLSearchParams(location.search)
    const clientId = params.get('client')
    if (clientId) return 'ID do Cliente'
    return 'Nome do Dispositivo'
  }

  const [searchFilterSelected, setSearchFilterSelected] = useState<string>(handleUrlFiltersOnSearch())

  function setCamerasPage(page: number) {
    _setCamerasPage((lastPage) => ({...lastPage, page}))
  }

  function setCamerasPageSize(pageSize: number) {
    _setCamerasPage((lastPage) => ({...lastPage, pageSize}))
  }

  function toggleObject(key: string) {
    setObjectItems(
      objectItems.map((it) => {
        if (it.key === key) {
          return {...it, selected: !it.selected}
        } else {
          return it
        }
      }),
    )
  }

  function toggleAllObjects() {
    const selected = !objectItems.every((it) => it.selected)
    setObjectItems(objectItems.map((it) => ({...it, selected})))
  }

  function addInterestArea(areaItem: AddAreaItem) {
    setAreaItems(
      areaItems.concat([
        {
          key: mkKey(),
          label: areaItem.name,
          points: areaItem.points,
          data: {adding: true},
        },
      ]),
    )
  }

  function deleteInterestArea(areaItem: AreaData) {
    setAreaItems(areaItems.filter((it) => it.key !== areaItem.key))
    if (areaItem.data?.area) {
      setAreaItemsDeleting(areaItemsDeleting.concat(areaItem))
    }
  }

  function editInterestArea(areaItem: AreaData) {
    const editAreaItem = {
      ...areaItem,
      data: {...areaItem.data, updated: true},
    }
    setAreaItems(areaItems.map((it) => (it.key === areaItem.key ? editAreaItem : it)))
  }

  async function deleteCameras() {
    if (selectedCamerasIds.length === 0) throw new Error('unreachable')
    try {
      await api.camera.deleteMany(selectedCamerasIds)
      setSelectedCamerasIds([])

      enqueueSnackbar(`Dispositivos removidos com sucesso`, {
        variant: 'success',
      })
      reloadCameras()
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao remover os dispositivos integrados')
    }
  }

  async function setCamerasStatus(option: boolean) {
    if (selectedCamerasIds.length === 0) throw new Error('unreachable')
    try {
      return await api.camera.setCamerasStatus(selectedCamerasIds, option)
    } catch (error) {
      const err = error as AxiosErrorGeneric
      const errorMessage = err.response.data.devMessage.includes('Quota limit')
        ? 'Cota atingida. Entre em contato com o suporte'
        : 'Erro ao alterar o status dos dispositivos integrados'

      handleErrorWithSnackbar(enqueueSnackbar, err, errorMessage)
    } finally {
      reloadCameras()
    }
  }

  const loadSnapshot = useCallback(async (ids: Array<number>) => {
    if (ids.length === 0) return
    api.camera.getConnetionTimes(ids).then((response) => {
      if (!response.body) return
      const reader = response.body.getReader()
      return new ReadableStream({
        start(controller) {
          function push() {
            reader.read().then(({done, value}) => {
              if (done) return controller.close()
              const splitedChunk = new TextDecoder().decode(value).split('\n')
              const connections = splitedChunk.filter((chunk) => chunk !== '').map((chunk) => JSON.parse(chunk))
              connections.forEach((connection: ConnectionTimeMultiple) => {
                if (Object.keys(connection).includes('id')) timeData[String(connection.id)] = Number(connection.connectionTime) / 1000
              })
              setTimeData(Object.assign({}, timeData))
              push()
            })
          }
          push()
        },
      })
    })
  }, [])

  const handleUrlFilters = () => {
    const params = new URLSearchParams(location.search)
    const clientId = params.get('client')
    if (clientId) return clientId
    return ''
  }
  const [camerasSearch, setCamerasSearch] = useState(handleUrlFilters())
  const camerasSearchDebounce = useDebounce(camerasSearch, 500)

  useEffect(() => {
    setCamerasPage(1)
  }, [camerasSearch, cameraStatusFilterDebounced, cameraSituationFilterDebounced])

  const [sortByLastDetection, setSortByLastDetection] = useState<boolean | undefined>()
  const loadCameras = async () => {
    try {
      if (blockLoadingCameras.current) return
      blockLoadingCameras.current = true
      const response = (
        await api.camera.getMany({
          page: camerasPage.page,
          pageSize: camerasPage.pageSize,
          filter: {
            companyIds: selectedCompanies.map((it) => it.id),
            cameraName: searchFilterSelected === 'Nome do Dispositivo' && camerasSearch ? [camerasSearch] : undefined,
            clientIds: searchFilterSelected === 'ID do Cliente' && camerasSearch ? [Number(camerasSearch)] : undefined,
            statuses: cameraStatusFilterDebounced ? cameraStatusFilterDebounced : undefined,
            isOnline: cameraSituationFilterDebounced,
          },
          includes: ['server', 'status', 'configuration', 'client', 'addressInfo'],
          search: {
            clientName: searchFilterSelected === 'Nome do Cliente' ? camerasSearch : undefined,
            serverName: searchFilterSelected === 'Nome do Servidor' ? camerasSearch : undefined,
          },
          sortByLastDetection,
        })
      ).data
      setCamerasTotalCount(response.data.totalPages ?? 0)
      setCameras(response.data.entities)
      const ids = response.data.entities.map((it) => it.id)
      if (ids.length > 0) loadSnapshot(ids)
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao carregar dispositivos integrados')
    } finally {
      setLoadingCameras(false)
      blockLoadingCameras.current = false
    }
  }

  useEffect(() => {
    setLoadingCameras(true)
    loadCameras()
  }, [
    camerasPage,
    reloadCamerasCount,
    selectedCompanies,
    camerasSearchDebounce,
    cameraStatusFilterDebounced,
    cameraSituationFilterDebounced,
    sortByLastDetection,
  ])

  const reloadCameras = () => {
    if (loadingCameras) return
    setLoadingCameras(true)
    setReloadCamerasCount((x) => x + 1)
  }

  const camerasPageChange = (page: Partial<Page>) => {
    _setCamerasPage((lastPage) => ({...lastPage, ...page}))
  }

  const showOptionsButton = selectedCamerasIds.length > 1

  function toggleStatusFilter(status: string) {
    const isChecked = chosenCameraStatusFilter?.includes(status.toUpperCase())
    if (isChecked) setChosenCameraStatusFilter(chosenCameraStatusFilter?.filter((it) => it.toLowerCase() !== status.toLowerCase()))
    else setChosenCameraStatusFilter((prev) => [...prev, status.toUpperCase()])
  }

  function toggleSituationFilter(situation: string) {
    const filterOnLineCamera = situation === 'Habilitada'
    if (chosenCameraFilterSituation !== undefined) setChosenCameraFilterSituation(undefined)
    else setChosenCameraFilterSituation(filterOnLineCamera)
  }

  return (
    <CamerasContext.Provider
      value={{
        setLoadingCameraStatus,
        toggleStatusFilter,
        toggleSituationFilter,
        camerasSearch,
        setCamerasSearch,
        selectedCamerasIds,
        setSelectedCamerasIds,
        showOptionsButton,
        deleteCameras,
        loadingCameras,
        camerasTotalCount,
        camerasPageChange,
        setCamerasPage,
        setCamerasPageSize,
        camerasPage,
        reloadCameras,
        cameras,
        objectItems,
        toggleObject,
        toggleAllObjects,
        areaItems,
        addInterestArea,
        deleteInterestArea,
        editInterestArea,
        cameraStatusFilterDebounced,
        searchFilterSelected,
        setSearchFilterSelected,
        setLoadingCameras,
        partition,
        setPartition,
        loadingCameraStatus,
        timeData,
        setCamerasStatus,
        setSortByLastDetection,
      }}>
      {children}
    </CamerasContext.Provider>
  )
}

export default CamerasProvider

export function useCameraContext() {
  return useContext(CamerasContext)
}
